import React, { useEffect, useState } from 'react';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import {
    CreateJobDialog,
    FileData,
    replaceModalConfig,
} from '../create-job-dialog';
import { Grid } from '@mui/material';
import {
    OwnersAndLimitedOwnersData,
    UploadFileFormInputs,
    styles,
    uploadFileDefaults,
    UploadFileSchema,
    operationFieldValues,
} from './upload-file-helper';
import { DragAndDrop, Autocomplete, TextField } from '@biq/apex-ui';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDictionaryList, useWalletList, useUploadFile } from 'hooks';
import { useAuth, usePermission } from '../../../../core';
import { useNavigate } from 'react-router-dom';
import { JOB_MANAGER_PATHS } from 'constants/job-manager-paths';
import { AutoCompleteOwnersAndLimitedOwnersField } from './AutocompleteField';
import { fileTypes } from 'pages/job-manager/job-manager-helper';
import JobExecutor from '../job-executor/JobExecutor';
import { validationsMessages } from 'lib';

export const UploadFile = (): React.JSX.Element => {
    const navigate = useNavigate();
    const { hasPermissions } = usePermission();
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [files, setFiles] = useState<FileData[]>([]);
    const [fileError, setFileError] = useState<boolean>(false);
    const [ownersAndLimitedOwnersData, setOwnersAndLimitedOwnersData] =
        useState<OwnersAndLimitedOwnersData[]>([]);
    const [filteredOwnersData, setFilteredOwnersData] = useState<
        OwnersAndLimitedOwnersData[]
    >([]);
    const [filteredLimitedOwnersData, setFilteredLimitedOwnersData] = useState<
        OwnersAndLimitedOwnersData[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { getToken, getUserEmail } = useAuth();
    const { mutate: uploadFileJob } = useUploadFile();
    const { data: chainsData, isLoading: chainsLoading } = useDictionaryList();
    const { data: walletsData, isLoading: walletsLoading } = useWalletList();

    const {
        handleSubmit,
        watch,
        control,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<UploadFileFormInputs>({
        mode: 'all',
        resolver: yupResolver(UploadFileSchema),
        defaultValues: uploadFileDefaults,
    });

    useEffect(() => {
        if (!walletsLoading && walletsData) {
            const userWallets = walletsData?.data?.userWallets ?? [];
            const systemWallets = walletsData?.data?.systemWallets ?? [];
            const combinedOwners = [
                ...userWallets.map((userWallet: any) => ({
                    id: userWallet.id ?? '',
                    value: userWallet.fullName ?? userWallet.email ?? '',
                    header: userWallet.fullName ?? '',
                    subtitle: userWallet.email ?? '',
                })),
                ...systemWallets.map((systemWallet: any) => ({
                    id: systemWallet.id ?? '',
                    value: systemWallet.id ?? '',
                    header: systemWallet.id ?? '',
                    subtitle: systemWallet.description ?? '',
                })),
            ];
            combinedOwners.sort((a: any, b: any) => {
                return a.header
                    .toLowerCase()
                    .localeCompare(b.header.toLowerCase());
            });
            setOwnersAndLimitedOwnersData(combinedOwners);
            setFilteredOwnersData(combinedOwners);
            setFilteredLimitedOwnersData(combinedOwners);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walletsLoading]);

    useEffect(() => {
        let filteredData = [...ownersAndLimitedOwnersData];
        const ownersSelectedData = watch('owners');
        const limitedOwnersSelectedData = watch('limitedOwners');
        setFilteredOwnersData(
            filteredData.filter(
                (data) =>
                    !limitedOwnersSelectedData.some(
                        (item: any) => data.id === item.id
                    )
            ) || []
        );
        setFilteredLimitedOwnersData(
            filteredData.filter(
                (data) =>
                    !ownersSelectedData.some((item: any) => data.id === item.id)
            ) || []
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watch('owners'), watch('limitedOwners')]);

    const handleStartUploadButton = (): void => {
        if (files.length === 0 || fileError) {
            return;
        }
        setIsLoading(true);
        setTimeout(() => {
            navigate(JOB_MANAGER_PATHS.LANDING_PAGE);
        }, 500);
        uploadFileJob(
            {
                file: files[0],
                token: getToken(),
                email: getUserEmail(),
                chain: getValues().chain,
                limitedOwners: getValues().limitedOwners.map(
                    (item: any) => item.id
                ),
                owners: getValues().owners.map((item: any) => item.id),
                operation: getValues().operation,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const onHandleSubmit = (): void => {
        if (files.length === 0) {
            setFileError(true);
        }
        handleSubmit(handleStartUploadButton)();
    };

    const onSubmit = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            onHandleSubmit();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    return (
        <CreateJobDialog
            buttonText='Start Upload'
            handleSubmit={onSubmit}
            isLoading={isLoading}
        >
            <Grid sx={styles.container}>
                <Grid sx={styles.bodyContainer}>
                    <Grid sx={styles.containerWidth}>
                        <JobExecutor />
                    </Grid>
                    <Grid sx={styles.containerWidth}>
                        <Controller
                            name='operation'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <Autocomplete
                                    {...field}
                                    options={operationFieldValues.map(
                                        (operation) => operation
                                    )}
                                    onChange={(__, value): void => {
                                        field.onChange(value);
                                    }}
                                    sx={styles.autoCompleteStyles}
                                    renderInput={(
                                        params
                                    ): React.JSX.Element => (
                                        <TextField
                                            {...params}
                                            size='medium'
                                            required={true}
                                            label='Operation'
                                            variant='outlined'
                                            error={!!errors.operation}
                                            errorText={
                                                errors.operation
                                                    ? validationsMessages.required
                                                    : ''
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid sx={styles.containerWidth}>
                        <Controller
                            name='chain'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <Autocomplete
                                    {...field}
                                    options={
                                        !chainsLoading && chainsData
                                            ? chainsData?.data.map(
                                                  (chain) => chain.name
                                              )
                                            : []
                                    }
                                    onChange={(__, value): void => {
                                        field.onChange(value);
                                    }}
                                    sx={styles.autoCompleteStyles}
                                    renderInput={(
                                        params
                                    ): React.JSX.Element => (
                                        <TextField
                                            {...params}
                                            size='medium'
                                            required={true}
                                            label='Chain'
                                            variant='outlined'
                                            error={!!errors.chain}
                                            errorText={
                                                errors.chain
                                                    ? validationsMessages.required
                                                    : ''
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid sx={styles.containerWidth}>
                        <Controller
                            name='fileType'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <Autocomplete
                                    {...field}
                                    options={fileTypes}
                                    onChange={(__, value): void => {
                                        field.onChange(value);
                                    }}
                                    sx={styles.autoCompleteStyles}
                                    renderInput={(
                                        params
                                    ): React.JSX.Element => (
                                        <TextField
                                            {...params}
                                            size='medium'
                                            required={true}
                                            label='File Type'
                                            variant='outlined'
                                            error={!!errors.fileType}
                                            errorText={
                                                errors.fileType
                                                    ? validationsMessages.required
                                                    : ''
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid sx={styles.containerWidth}>
                        <AutoCompleteOwnersAndLimitedOwnersField
                            name={'owners'}
                            label={'Owners'}
                            control={control}
                            filteredOwnersData={filteredOwnersData}
                            ownersAndLimitedOwnersData={
                                ownersAndLimitedOwnersData
                            }
                            fieldValue={watch('owners')}
                            setValue={setValue}
                        />
                    </Grid>
                    <Grid sx={styles.containerWidth}>
                        <AutoCompleteOwnersAndLimitedOwnersField
                            name={'limitedOwners'}
                            label={'Limited Owners'}
                            control={control}
                            filteredOwnersData={filteredLimitedOwnersData}
                            ownersAndLimitedOwnersData={
                                ownersAndLimitedOwnersData
                            }
                            fieldValue={watch('limitedOwners')}
                            setValue={setValue}
                        />
                    </Grid>
                    <Grid sx={styles.dragAndDropContainerWidth}>
                        <DragAndDrop
                            files={files}
                            setFiles={setFiles}
                            fileError={fileError}
                            setFileError={setFileError}
                            filePreview={true}
                            showReplaceFileModal={true}
                            replaceModalConfig={replaceModalConfig}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateJobDialog>
    );
};
